/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React from "react";
import { nanoid } from "nanoid";
import getFileExtension from "utils/getFileExtension";
import getFileMIME from "utils/getFileMIME";
import toast from "react-hot-toast";
import validateAwsKey from "utils/validateAwsKey";
import cn from "classnames";

window.AWS.config.update({
  accessKeyId: import.meta.env.VITE_S3_ACCESS_ID,
  secretAccessKey: import.meta.env.VITE_S3_SECRET_KEY,
});

const AwsUploader = ({
  cloudKeyName,
  onUpload,
  onUploadStart,
  onError,
  onPreview,
  onPreviewStart,
  children,
  acceptedFormats = ["pdf", "png", "jpeg", "jpg", "webp"],
  disabled = false,
}) => {
  const s3Ref = React.useRef(null);
  const fileInputRef = React.useRef();
  const isDisabledRef = React.useRef(false);
  React.useEffect(() => {
    s3Ref.current = new window.AWS.S3({
      params: { Bucket: import.meta.env.VITE_S3_BUCKET_NAME },
      region: import.meta.env.VITE_S3_REGION,
    });
  }, []);

  React.useEffect(() => {
    if (cloudKeyName && cloudKeyName.length > 0) {
      if (onPreview && !isDisabledRef.current) {
        isDisabledRef.current = true;
        handlePreview(cloudKeyName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudKeyName, disabled]);

  const handlePreview = async (key) => {
    if (!validateAwsKey(key)) return;
    if (onPreviewStart) onPreviewStart();
    const params = {
      Bucket: import.meta.env.VITE_S3_BUCKET_NAME,
      Key: key,
    };

    await s3Ref.current.getObject(params, (err, data) => {
      if (err) {
        toast.error("An error happened, please report it to the admin");
        if (onError) onError();
        return;
      }

      const extension = getFileExtension(key);
      const mimeType = getFileMIME(key);
      const blob = new Blob([data.Body], { type: mimeType });
      const url = URL.createObjectURL(blob);
      if (onPreview) {
        onPreview(url, extension);
      }
    });
  };

  const handleUpload = (e) => {
    e.preventDefault();
    const {
      target: {
        files: [file],
      },
    } = e;

    if (!file) return;
    const mimeType = getFileMIME(file.name);
    const extension = getFileExtension(file.name);
    if (extension.length === 0 || mimeType.length === 0) {
      toast.error("unsupported file type");
      return;
    }

    if (acceptedFormats && acceptedFormats.length > 0) {
      if (!acceptedFormats.includes(extension)) {
        toast.error("unsupported file type");
        return;
      }
    }

    let key;
    if (extension.length > 0) {
      key = nanoid() + "." + extension;
    } else {
      key = nanoid();
    }

    const params = {
      ACL: "public-read",
      Body: file,
      Bucket: import.meta.env.VITE_S3_BUCKET_NAME,
      Key: key,
    };

    if (onUploadStart) {
      onUploadStart();
    }

    s3Ref.current.putObject(params, (err) => {
      if (err) {
        toast.error("An error happened, please report it to the admin");
        if (onError) onError();
        return;
      }

      if (onUpload) {
        const imageURL = URL.createObjectURL(file);
        isDisabledRef.current = true;
        onUpload(key, imageURL, extension);
      }
    });

    fileInputRef.current.value = "";
  };

  return (
    <div
      role="button"
      className={cn({ "cursor-default": disabled })}
      onClick={() => {
        if (!disabled) fileInputRef.current.click();
      }}
      test-id="wIns813m"
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleUpload}
        className="hidden"
        test-id="6HIsnyLh"
      />

      {children}
    </div>
  );
};

export default AwsUploader;
